import React, { useState } from 'react';
import { Upload, AlertCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import type { Chapter } from '../../../types';

interface AssignmentFormProps {
  ideaId: string;
  applications: any[];
  chapters: Chapter[];
  onSubmit: (data: {
    applicationId: string;
    readerId: string;
    title: string;
    description?: string;
    chapterId?: string;
    documentUrl?: string;
    dueDate?: string;
  }) => Promise<void>;
  onCancel: () => void;
}

export default function AssignmentForm({
  ideaId,
  applications,
  chapters,
  onSubmit,
  onCancel
}: AssignmentFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedApplication, setSelectedApplication] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [document, setDocument] = useState<File | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setDocument(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !selectedApplication) return;

    try {
      setLoading(true);
      setError(null);

      const application = applications.find(a => a.id === selectedApplication);
      if (!application) {
        throw new Error('Selected application not found');
      }

      let documentUrl: string | undefined;
      if (document) {
        // Use the original file name directly
        const fileName = `${ideaId}/${application.reader_id}/${document.name}`;
        
        const { error: uploadError } = await supabase.storage
          .from('beta-documents')
          .upload(fileName, document, {
            upsert: true,
            contentType: document.type // Preserve the content type
          });

        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('beta-documents')
          .getPublicUrl(fileName);
          
        documentUrl = publicUrl;
      }

      await onSubmit({
        applicationId: application.id,
        readerId: application.reader_id,
        title,
        description: description || undefined,
        chapterId: selectedChapter || undefined,
        documentUrl,
        dueDate: dueDate || undefined
      });

      onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create assignment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div className="text-red-700">{error}</div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Select Beta Reader
        </label>
        <select
          value={selectedApplication}
          onChange={(e) => setSelectedApplication(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          required
        >
          <option value="">Select a beta reader</option>
          {applications.map((app) => (
            <option key={app.id} value={app.id}>
              {app.profiles.full_name || app.profiles.username}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Assignment Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="e.g., Review First Three Chapters"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description (Optional)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          rows={3}
          placeholder="Add any specific instructions or notes for the beta reader..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Chapter (Optional)
          </label>
          <select
            value={selectedChapter}
            onChange={(e) => setSelectedChapter(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">No specific chapter</option>
            {chapters.map((chapter) => (
              <option key={chapter.id} value={chapter.id}>
                {chapter.title}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Due Date (Optional)
          </label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload Document (Optional)
        </label>
        <div className="flex items-center gap-4">
          {document && (
            <div className="text-sm text-gray-600">
              Selected: {document.name}
            </div>
          )}
          <label className="flex-shrink-0">
            <div className="px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 flex items-center gap-2">
              <Upload className="w-4 h-4 text-gray-400" />
              Choose file
            </div>
            <input
              type="file"
              className="hidden"
              onChange={handleDocumentChange}
              accept=".pdf,.doc,.docx,.txt,.rtf"
            />
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 hover:text-gray-900"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !title || !selectedApplication}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Create Assignment'}
        </button>
      </div>
    </form>
  );
}